import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { MapPinIcon, FilterIcon, CalendarIcon, ClockIcon, UserIcon, MaximizeIcon, CheckCircleIcon } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { PageHeader } from '../components/ui/PageHeader';
import { LeafletMap } from '../components/ui/LeafletMap';
import { useApp } from '../context/AppContext';
import L from 'leaflet';
import { Visit } from '../types';
// ── Static demo data (2026) shown when no real GPS visits exist ──────────────
const DEMO_VISITS: Visit[] = [{
  id: 'demo-1',
  vendedorId: 'v1',
  vendedorNombre: 'María García',
  clienteId: 'c1',
  clienteNombre: 'Farmacia Carol',
  zona: 'Zona Colonial',
  tipoVisita: 'cobro_y_venta',
  fecha: new Date('2026-02-20T09:00:00'),
  horaLlegada: new Date('2026-02-20T09:00:00'),
  horaSalida: new Date('2026-02-20T09:45:00'),
  tiempoTotal: 45,
  ubicacionLlegada: {
    lat: 18.4731,
    lng: -69.8836,
    timestamp: new Date('2026-02-20T09:00:00')
  },
  ubicacionSalida: null,
  cobro: {
    huboCobo: true,
    montoCobrado: 15000,
    metodoPago: 'transferencia',
    numeroFactura: 'FAC-001',
    estadoFactura: 'pagada_completa',
    credito: 0,
    descuento: 0
  },
  venta: {
    huboVenta: true,
    montoVenta: 8500,
    numeroPedido: 'PED-001'
  },
  comentarios: 'Cliente satisfecho, solicita más producto',
  motivoNoPago: '',
  proximaVisita: null,
  estado: 'completada'
}, {
  id: 'demo-2',
  vendedorId: 'v1',
  vendedorNombre: 'María García',
  clienteId: 'c2',
  clienteNombre: 'Farmacia La Salud',
  zona: 'Piantini',
  tipoVisita: 'cobro',
  fecha: new Date('2026-02-20T10:30:00'),
  horaLlegada: new Date('2026-02-20T10:30:00'),
  horaSalida: new Date('2026-02-20T11:00:00'),
  tiempoTotal: 30,
  ubicacionLlegada: {
    lat: 18.4755,
    lng: -69.928,
    timestamp: new Date('2026-02-20T10:30:00')
  },
  ubicacionSalida: null,
  cobro: {
    huboCobo: true,
    montoCobrado: 5000,
    metodoPago: 'efectivo',
    numeroFactura: 'FAC-002',
    estadoFactura: 'pago_parcial',
    credito: 0,
    descuento: 0
  },
  venta: {
    huboVenta: false,
    montoVenta: 0,
    numeroPedido: ''
  },
  comentarios: 'Pago parcial',
  motivoNoPago: '',
  proximaVisita: null,
  estado: 'completada'
}, {
  id: 'demo-3',
  vendedorId: 'v2',
  vendedorNombre: 'Juan Pérez',
  clienteId: 'c3',
  clienteNombre: 'Farmacia del Pueblo',
  zona: 'Santiago Centro',
  tipoVisita: 'venta',
  fecha: new Date('2026-02-20T09:15:00'),
  horaLlegada: new Date('2026-02-20T09:15:00'),
  horaSalida: new Date('2026-02-20T10:00:00'),
  tiempoTotal: 45,
  ubicacionLlegada: {
    lat: 19.4517,
    lng: -70.697,
    timestamp: new Date('2026-02-20T09:15:00')
  },
  ubicacionSalida: null,
  cobro: {
    huboCobo: false,
    montoCobrado: 0,
    metodoPago: null,
    numeroFactura: '',
    estadoFactura: 'no_pago',
    credito: 0,
    descuento: 0
  },
  venta: {
    huboVenta: true,
    montoVenta: 12000,
    numeroPedido: 'PED-002'
  },
  comentarios: 'Nuevo pedido grande',
  motivoNoPago: '',
  proximaVisita: null,
  estado: 'completada'
}, {
  id: 'demo-4',
  vendedorId: 'v2',
  vendedorNombre: 'Juan Pérez',
  clienteId: 'c4',
  clienteNombre: 'Farmacia Central',
  zona: 'Naco',
  tipoVisita: 'seguimiento',
  fecha: new Date('2026-02-24T11:00:00'),
  horaLlegada: new Date('2026-02-24T11:00:00'),
  horaSalida: null,
  tiempoTotal: null,
  ubicacionLlegada: {
    lat: 18.48,
    lng: -69.94,
    timestamp: new Date('2026-02-24T11:00:00')
  },
  ubicacionSalida: null,
  cobro: {
    huboCobo: false,
    montoCobrado: 0,
    metodoPago: null,
    numeroFactura: '',
    estadoFactura: 'no_pago',
    credito: 0,
    descuento: 0
  },
  venta: {
    huboVenta: false,
    montoVenta: 0,
    numeroPedido: ''
  },
  comentarios: 'En visita ahora',
  motivoNoPago: '',
  proximaVisita: null,
  estado: 'en_progreso'
}, {
  id: 'demo-5',
  vendedorId: 'v3',
  vendedorNombre: 'Ana López',
  clienteId: 'c5',
  clienteNombre: 'Farmacia Nueva Vida',
  zona: 'La Romana',
  tipoVisita: 'cobro',
  fecha: new Date('2026-02-24T09:30:00'),
  horaLlegada: new Date('2026-02-24T09:30:00'),
  horaSalida: null,
  tiempoTotal: null,
  ubicacionLlegada: {
    lat: 18.4273,
    lng: -68.9728,
    timestamp: new Date('2026-02-24T09:30:00')
  },
  ubicacionSalida: null,
  cobro: {
    huboCobo: false,
    montoCobrado: 0,
    metodoPago: null,
    numeroFactura: 'FAC-003',
    estadoFactura: 'no_pago',
    credito: 0,
    descuento: 0
  },
  venta: {
    huboVenta: false,
    montoVenta: 0,
    numeroPedido: ''
  },
  comentarios: '',
  motivoNoPago: '',
  proximaVisita: null,
  estado: 'en_progreso'
}, {
  id: 'demo-6',
  vendedorId: 'v3',
  vendedorNombre: 'Ana López',
  clienteId: 'c6',
  clienteNombre: 'Farmacia El Ángel',
  zona: 'San Pedro de Macorís',
  tipoVisita: 'cobro_y_venta',
  fecha: new Date('2026-02-19T14:00:00'),
  horaLlegada: new Date('2026-02-19T14:00:00'),
  horaSalida: new Date('2026-02-19T15:00:00'),
  tiempoTotal: 60,
  ubicacionLlegada: {
    lat: 18.4538,
    lng: -69.3051,
    timestamp: new Date('2026-02-19T14:00:00')
  },
  ubicacionSalida: null,
  cobro: {
    huboCobo: true,
    montoCobrado: 22000,
    metodoPago: 'cheque',
    numeroFactura: 'FAC-004',
    estadoFactura: 'pagada_completa',
    credito: 0,
    descuento: 0
  },
  venta: {
    huboVenta: true,
    montoVenta: 15000,
    numeroPedido: 'PED-003'
  },
  comentarios: 'Excelente relación con el cliente',
  motivoNoPago: '',
  proximaVisita: null,
  estado: 'completada'
}, {
  id: 'demo-7',
  vendedorId: 'v1',
  vendedorNombre: 'María García',
  clienteId: 'c7',
  clienteNombre: 'Farmacia Bienestar',
  zona: 'Puerto Plata',
  tipoVisita: 'venta',
  fecha: new Date('2026-02-18T12:00:00'),
  horaLlegada: new Date('2026-02-18T12:00:00'),
  horaSalida: new Date('2026-02-18T12:35:00'),
  tiempoTotal: 35,
  ubicacionLlegada: {
    lat: 19.7933,
    lng: -70.6893,
    timestamp: new Date('2026-02-18T12:00:00')
  },
  ubicacionSalida: null,
  cobro: {
    huboCobo: false,
    montoCobrado: 0,
    metodoPago: null,
    numeroFactura: '',
    estadoFactura: 'no_pago',
    credito: 0,
    descuento: 0
  },
  venta: {
    huboVenta: true,
    montoVenta: 6200,
    numeroPedido: 'PED-010'
  },
  comentarios: 'Pedido de reposición mensual',
  motivoNoPago: '',
  proximaVisita: null,
  estado: 'completada'
}, {
  id: 'demo-8',
  vendedorId: 'v2',
  vendedorNombre: 'Juan Pérez',
  clienteId: 'c8',
  clienteNombre: 'Farmacia Express',
  zona: 'Los Prados',
  tipoVisita: 'cobro_y_venta',
  fecha: new Date('2026-02-21T08:00:00'),
  horaLlegada: new Date('2026-02-21T08:00:00'),
  horaSalida: new Date('2026-02-21T08:50:00'),
  tiempoTotal: 50,
  ubicacionLlegada: {
    lat: 18.495,
    lng: -69.91,
    timestamp: new Date('2026-02-21T08:00:00')
  },
  ubicacionSalida: null,
  cobro: {
    huboCobo: true,
    montoCobrado: 8500,
    metodoPago: 'efectivo',
    numeroFactura: 'FAC-010',
    estadoFactura: 'pagada_completa',
    credito: 0,
    descuento: 0
  },
  venta: {
    huboVenta: true,
    montoVenta: 4300,
    numeroPedido: 'PED-011'
  },
  comentarios: 'Buen cliente, siempre puntual',
  motivoNoPago: '',
  proximaVisita: null,
  estado: 'completada'
}, {
  id: 'demo-9',
  vendedorId: 'v3',
  vendedorNombre: 'Ana López',
  clienteId: 'c9',
  clienteNombre: 'Farmacia Gazcue',
  zona: 'Gazcue',
  tipoVisita: 'venta',
  fecha: new Date('2026-02-23T15:00:00'),
  horaLlegada: new Date('2026-02-23T15:00:00'),
  horaSalida: new Date('2026-02-23T15:45:00'),
  tiempoTotal: 45,
  ubicacionLlegada: {
    lat: 18.468,
    lng: -69.905,
    timestamp: new Date('2026-02-23T15:00:00')
  },
  ubicacionSalida: null,
  cobro: {
    huboCobo: false,
    montoCobrado: 0,
    metodoPago: null,
    numeroFactura: '',
    estadoFactura: 'no_pago',
    credito: 0,
    descuento: 0
  },
  venta: {
    huboVenta: true,
    montoVenta: 18500,
    numeroPedido: 'PED-020'
  },
  comentarios: 'Pedido especial temporada alta',
  motivoNoPago: '',
  proximaVisita: null,
  estado: 'completada'
}, {
  id: 'demo-10',
  vendedorId: 'v2',
  vendedorNombre: 'Juan Pérez',
  clienteId: 'c10',
  clienteNombre: 'Farmacia Bella Vista',
  zona: 'Bella Vista',
  tipoVisita: 'cobro_y_venta',
  fecha: new Date('2026-02-24T08:30:00'),
  horaLlegada: new Date('2026-02-24T08:30:00'),
  horaSalida: null,
  tiempoTotal: null,
  ubicacionLlegada: {
    lat: 18.462,
    lng: -69.935,
    timestamp: new Date('2026-02-24T08:30:00')
  },
  ubicacionSalida: null,
  cobro: {
    huboCobo: false,
    montoCobrado: 0,
    metodoPago: null,
    numeroFactura: '',
    estadoFactura: 'no_pago',
    credito: 0,
    descuento: 0
  },
  venta: {
    huboVenta: false,
    montoVenta: 0,
    numeroPedido: ''
  },
  comentarios: '',
  motivoNoPago: '',
  proximaVisita: null,
  estado: 'en_progreso'
}, {
  id: 'demo-11',
  vendedorId: 'v1',
  vendedorNombre: 'María García',
  clienteId: 'c11',
  clienteNombre: 'Farmacia San Isidro',
  zona: 'San Isidro',
  tipoVisita: 'cobro',
  fecha: new Date('2026-02-22T10:00:00'),
  horaLlegada: new Date('2026-02-22T10:00:00'),
  horaSalida: new Date('2026-02-22T10:30:00'),
  tiempoTotal: 30,
  ubicacionLlegada: {
    lat: 18.465,
    lng: -69.82,
    timestamp: new Date('2026-02-22T10:00:00')
  },
  ubicacionSalida: null,
  cobro: {
    huboCobo: true,
    montoCobrado: 9200,
    metodoPago: 'transferencia',
    numeroFactura: 'FAC-015',
    estadoFactura: 'pagada_completa',
    credito: 0,
    descuento: 0
  },
  venta: {
    huboVenta: false,
    montoVenta: 0,
    numeroPedido: ''
  },
  comentarios: 'Cobro rápido y efectivo',
  motivoNoPago: '',
  proximaVisita: null,
  estado: 'completada'
}, {
  id: 'demo-12',
  vendedorId: 'v3',
  vendedorNombre: 'Ana López',
  clienteId: 'c12',
  clienteNombre: 'Farmacia Miramar',
  zona: 'Miramar',
  tipoVisita: 'cobro',
  fecha: new Date('2026-02-17T11:00:00'),
  horaLlegada: new Date('2026-02-17T11:00:00'),
  horaSalida: new Date('2026-02-17T11:20:00'),
  tiempoTotal: 20,
  ubicacionLlegada: {
    lat: 18.485,
    lng: -69.96,
    timestamp: new Date('2026-02-17T11:00:00')
  },
  ubicacionSalida: null,
  cobro: {
    huboCobo: false,
    montoCobrado: 0,
    metodoPago: null,
    numeroFactura: 'FAC-020',
    estadoFactura: 'no_pago',
    credito: 0,
    descuento: 0
  },
  venta: {
    huboVenta: false,
    montoVenta: 0,
    numeroPedido: ''
  },
  comentarios: 'Cliente ausente, reprogramar',
  motivoNoPago: 'Encargado no disponible',
  proximaVisita: null,
  estado: 'cancelada'
}];
// ── Tile configs ──────────────────────────────────────────────────────────────
const TILE_CONFIGS = {
  streets: {
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png',
    attribution: '&copy; OpenStreetMap &copy; CARTO',
    subdomains: 'abcd'
  },
  osm: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; OpenStreetMap contributors',
    subdomains: 'abc'
  },
  google: {
    url: 'https://mt{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
    attribution: '&copy; Google Maps',
    subdomains: '0123'
  },
  'google-sat': {
    url: 'https://mt{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}',
    attribution: '&copy; Google Maps',
    subdomains: '0123'
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: '&copy; Esri, Maxar, Earthstar Geographics',
    subdomains: ''
  },
  dark: {
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
    attribution: '&copy; OpenStreetMap &copy; CARTO',
    subdomains: 'abcd'
  }
};
// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtTime(date: Date | null) {
  if (!date) return '--:--';
  return new Date(date).toLocaleTimeString('es-DO', {
    hour: '2-digit',
    minute: '2-digit'
  });
}
function fmtDate(date: Date) {
  return new Date(date).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short'
  });
}
function fmtCurrency(n: number) {
  return new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 0
  }).format(n);
}
function statusColor(s: string) {
  if (s === 'completada') return '#059669';
  if (s === 'en_progreso') return '#d97706';
  return '#dc2626';
}
function statusBgClass(s: string) {
  if (s === 'completada') return 'bg-emerald-500';
  if (s === 'en_progreso') return 'bg-amber-500';
  return 'bg-red-500';
}
function visitTypeLabel(t: string) {
  return ({
    cobro: 'Cobro',
    venta: 'Venta',
    cobro_y_venta: 'Cobro y Venta',
    seguimiento: 'Seguimiento'
  } as any)[t] ?? t;
}
// ── Component ─────────────────────────────────────────────────────────────────
export function MapPage() {
  const {
    visits,
    users,
    updateVisit,
    getCurrentLocation
  } = useApp();
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.LayerGroup | null>(null);
  // Store latest visitsWithGPS in a ref so updateMarkers never has stale data
  const visitsRef = useRef<Visit[]>([]);
  const [dateFilter, setDateFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [selectedVisitId, setSelectedVisitId] = useState<string | null>(null);
  const [showCancelled, setShowCancelled] = useState(false);
  const [mapStyle, setMapStyle] = useState<'streets' | 'osm' | 'google' | 'google-sat' | 'satellite' | 'dark'>('streets');
  const [completingVisitId, setCompletingVisitId] = useState<string | null>(null);
  const activeVisits = visits.filter(v => v.estado === 'en_progreso');
  const vendors = users.filter(u => u.activo && u.permisos?.['new-visit'] === true);
  const vendorOptions = [{
    value: '',
    label: 'Todos los vendedores'
  }, ...vendors.map(v => ({
    value: v.id,
    label: v.nombre
  }))];
  // Compute filtered visits
  const realGPS = visits.filter(v => v.ubicacionLlegada).filter(v => showCancelled || v.estado !== 'cancelada').filter(v => {
    if (vendorFilter && v.vendedorId !== vendorFilter) return false;
    if (dateFilter) {
      const d = new Date(v.fecha).toISOString().split('T')[0];
      if (d !== dateFilter) return false;
    }
    return true;
  }).sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  const isDemo = realGPS.length === 0 && !dateFilter && !vendorFilter;
  const visitsWithGPS = isDemo ? DEMO_VISITS.filter(v => showCancelled || v.estado !== 'cancelada') : realGPS;
  // Keep ref in sync with latest computed value
  visitsRef.current = visitsWithGPS;
  // ── Tile style switcher ────────────────────────────────────────────────────
  const applyTiles = useCallback((map: L.Map, style: 'streets' | 'osm' | 'google' | 'google-sat' | 'satellite' | 'dark') => {
    map.eachLayer(l => {
      if (l instanceof L.TileLayer) map.removeLayer(l);
    });
    const cfg = TILE_CONFIGS[style];
    const opts: any = {
      attribution: cfg.attribution,
      maxZoom: 20
    };
    if (cfg.subdomains) opts.subdomains = cfg.subdomains;
    L.tileLayer(cfg.url, opts).addTo(map);
  }, []);
  // ── Marker renderer — reads from ref, never stale ─────────────────────────
  const renderMarkers = useCallback(() => {
    const map = mapRef.current;
    const layer = markersRef.current;
    if (!map || !layer) return;
    const visits = visitsRef.current;
    layer.clearLayers();
    const bounds: L.LatLngExpression[] = [];
    visits.forEach(visit => {
      if (!visit.ubicacionLlegada) return;
      const {
        lat,
        lng
      } = visit.ubicacionLlegada;
      bounds.push([lat, lng]);
      const color = statusColor(visit.estado);
      const sel = visit.id === selectedVisitId;
      const sz = sel ? 36 : 28;
      const icon = L.divIcon({
        html: `
          <div style="position:relative;width:${sz}px;height:${sz * 1.33}px;">
            <svg viewBox="0 0 24 32" width="${sz}" height="${sz * 1.33}" xmlns="http://www.w3.org/2000/svg"
              style="filter:drop-shadow(0 2px 4px rgba(0,0,0,0.35));display:block;">
              <path d="M12 0C7.58 0 4 3.58 4 8c0 5.25 8 16 8 16s8-10.75 8-16c0-4.42-3.58-8-8-8z"
                fill="${color}" stroke="white" stroke-width="1.5"/>
              <circle cx="12" cy="8" r="3.5" fill="white" opacity="0.95"/>
            </svg>
            ${sel ? `<div style="position:absolute;top:-4px;right:-4px;width:10px;height:10px;
              background:#fff;border:2px solid ${color};border-radius:50%;"></div>` : ''}
          </div>`,
        className: '',
        iconSize: [sz, sz * 1.33],
        iconAnchor: [sz / 2, sz * 1.33],
        popupAnchor: [0, -(sz * 1.33)]
      });
      const statusBg = visit.estado === 'completada' ? '#dcfce7' : visit.estado === 'en_progreso' ? '#fef3c7' : '#fee2e2';
      const statusTxt = visit.estado === 'completada' ? '#166534' : visit.estado === 'en_progreso' ? '#92400e' : '#991b1b';
      const statusLbl = visit.estado === 'completada' ? 'Completada' : visit.estado === 'en_progreso' ? 'En curso' : 'Cancelada';
      const cobradoHtml = visit.cobro.huboCobo ? `<div style="display:flex;align-items:center;gap:6px;margin-top:6px;padding:6px 8px;background:#f0fdf4;border-radius:8px;">
            <span style="color:#059669;font-size:11px;">💰</span>
            <span style="color:#059669;font-weight:700;font-size:12px;">${fmtCurrency(visit.cobro.montoCobrado)}</span>
           </div>` : '';
      const vendidoHtml = visit.venta.huboVenta ? `<div style="display:flex;align-items:center;gap:6px;margin-top:4px;padding:6px 8px;background:#eff6ff;border-radius:8px;">
            <span style="font-size:11px;">🛒</span>
            <span style="color:#2563eb;font-weight:700;font-size:12px;">${fmtCurrency(visit.venta.montoVenta)}</span>
           </div>` : '';
      const marker = L.marker([lat, lng], {
        icon
      });
      const googleMapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
      const googleDirectionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
      marker.bindPopup(`
        <div style="min-width:220px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;padding:4px;">
          <div style="font-weight:800;font-size:14px;color:#0f172a;margin-bottom:2px;">${visit.clienteNombre}</div>
          <div style="color:#64748b;font-size:11px;margin-bottom:8px;">${visit.vendedorNombre} · ${visit.zona}</div>
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;">
            <span style="background:${statusBg};color:${statusTxt};padding:2px 10px;border-radius:20px;font-size:11px;font-weight:700;">${statusLbl}</span>
            <span style="color:#94a3b8;font-size:11px;">${visitTypeLabel(visit.tipoVisita)}</span>
          </div>
          <div style="color:#94a3b8;font-size:11px;">
            📅 ${fmtDate(visit.fecha)} · ⏰ ${fmtTime(visit.horaLlegada)}${visit.tiempoTotal ? ` · ${visit.tiempoTotal} min` : ''}
          </div>
          ${cobradoHtml}${vendidoHtml}
          ${visit.comentarios ? `<div style="font-size:11px;color:#94a3b8;margin-top:8px;padding-top:8px;border-top:1px solid #f1f5f9;">${visit.comentarios}</div>` : ''}
          <div style="display:flex;gap:6px;margin-top:10px;padding-top:8px;border-top:1px solid #f1f5f9;">
            <a href="${googleMapsUrl}" target="_blank" rel="noopener noreferrer"
              style="flex:1;display:flex;align-items:center;justify-content:center;gap:4px;padding:6px 8px;background:#4285f4;color:white;border-radius:8px;font-size:11px;font-weight:600;text-decoration:none;cursor:pointer;">
              📍 Ver en Maps
            </a>
            <a href="${googleDirectionsUrl}" target="_blank" rel="noopener noreferrer"
              style="flex:1;display:flex;align-items:center;justify-content:center;gap:4px;padding:6px 8px;background:#34a853;color:white;border-radius:8px;font-size:11px;font-weight:600;text-decoration:none;cursor:pointer;">
              🧭 Cómo llegar
            </a>
          </div>
        </div>`, {
        maxWidth: 280,
        className: 'modern-popup'
      });
      marker.on('click', () => setSelectedVisitId(visit.id));
      layer.addLayer(marker);
    });
    if (bounds.length > 0 && !selectedVisitId) {
      map.fitBounds(bounds as L.LatLngBoundsExpression, {
        padding: [50, 50],
        maxZoom: 15
      });
    }
  }, [selectedVisitId]); // only selectedVisitId matters for icon size
  // ── Map ready callback ────────────────────────────────────────────────────
  const handleMapReady = useCallback((map: L.Map) => {
    mapRef.current = map;
    markersRef.current = L.layerGroup().addTo(map);
    applyTiles(map, mapStyle);
    // Small delay to ensure the map container is fully painted
    setTimeout(() => {
      map.invalidateSize();
      renderMarkers();
    }, 150);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // ── Re-render markers when data or selection changes ──────────────────────
  useEffect(() => {
    renderMarkers();
  }, [visitsWithGPS, selectedVisitId, renderMarkers]);
  // ── Tile style switch ─────────────────────────────────────────────────────
  useEffect(() => {
    if (mapRef.current) applyTiles(mapRef.current, mapStyle);
  }, [mapStyle, applyTiles]);
  // ── Quick complete ────────────────────────────────────────────────────────
  const handleQuickComplete = async (visit: Visit) => {
    setCompletingVisitId(visit.id);
    try {
      const location = await getCurrentLocation();
      const now = new Date();
      const start = new Date(visit.horaLlegada || visit.fecha);
      await updateVisit(visit.id, {
        estado: 'completada',
        horaSalida: now,
        tiempoTotal: Math.max(1, Math.round((now.getTime() - start.getTime()) / 60000)),
        ubicacionSalida: location || undefined
      });
    } catch (e) {
      alert('Error al finalizar la visita.');
    } finally {
      setCompletingVisitId(null);
    }
  };
  // ── Center on visit ───────────────────────────────────────────────────────
  const centerOnVisit = (visitId: string) => {
    const v = visitsWithGPS.find(x => x.id === visitId);
    if (v?.ubicacionLlegada && mapRef.current) {
      mapRef.current.setView([v.ubicacionLlegada.lat, v.ubicacionLlegada.lng], 16, {
        animate: true
      });
      setSelectedVisitId(visitId);
    }
  };
  const resetView = () => {
    if (!mapRef.current) return;
    const bounds = visitsWithGPS.filter(v => v.ubicacionLlegada).map(v => [v.ubicacionLlegada!.lat, v.ubicacionLlegada!.lng] as L.LatLngExpression);
    if (bounds.length > 0) {
      mapRef.current.fitBounds(bounds as L.LatLngBoundsExpression, {
        padding: [50, 50],
        maxZoom: 15
      });
    }
    setSelectedVisitId(null);
  };
  return <div className="space-y-6" data-id="element-2181">
      <PageHeader title="Mapa de Visitas" subtitle="Ubicación en tiempo real y seguimiento GPS" icon={<MapPinIcon className="w-6 h-6" data-id="element-2183" />} data-id="element-2182" />

      {/* Active visits */}
      {activeVisits.length > 0 && <div className="animate-stagger-1" data-id="element-2184">
          <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2 font-heading" data-id="element-2185">
            <span className="relative flex h-3 w-3" data-id="element-2186">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" data-id="element-2187" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500" data-id="element-2188" />
            </span>
            Visitas en Curso ({activeVisits.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2189">
            {activeVisits.map(visit => <div key={visit.id} className="bg-white rounded-xl border border-amber-100 shadow-sm p-4 flex flex-col justify-between relative overflow-hidden" data-id="element-2190">
                <div className="absolute top-0 left-0 w-1 h-full bg-amber-500" data-id="element-2191" />
                <div className="pl-2" data-id="element-2192">
                  <div className="flex items-start justify-between mb-2" data-id="element-2193">
                    <Badge variant="warning" size="sm" className="animate-pulse" data-id="element-2194">
                      En Progreso
                    </Badge>
                    <div className="text-right" data-id="element-2195">
                      <span className="text-xs font-bold text-slate-500 block" data-id="element-2196">
                        Inicio
                      </span>
                      <span className="text-xs text-slate-700 font-mono" data-id="element-2197">
                        {fmtTime(visit.horaLlegada)}
                      </span>
                    </div>
                  </div>
                  <h4 className="font-bold text-slate-900 text-lg mb-1 truncate font-heading" data-id="element-2198">
                    {visit.clienteNombre}
                  </h4>
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-4" data-id="element-2199">
                    <UserIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-2200" />
                    <span className="font-medium" data-id="element-2201">{visit.vendedorNombre}</span>
                    <span className="text-slate-300" data-id="element-2202">•</span>
                    <span className="text-xs bg-slate-100 px-2 py-0.5 rounded text-slate-600" data-id="element-2203">
                      {visit.zona}
                    </span>
                  </div>
                </div>
                <div className="pl-2 mt-auto" data-id="element-2204">
                  <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white border-transparent" onClick={() => handleQuickComplete(visit)} isLoading={completingVisitId === visit.id} leftIcon={<CheckCircleIcon className="w-4 h-4" data-id="element-2206" />} data-id="element-2205">
                    {completingVisitId === visit.id ? 'Finalizando...' : 'Marcar Completada'}
                  </Button>
                  <p className="text-[10px] text-center text-slate-400 mt-2" data-id="element-2207">
                    Se registrará ubicación GPS actual
                  </p>
                </div>
              </div>)}
          </div>
        </div>}

      {/* Filters */}
      <Card className="animate-stagger-2" data-id="element-2208">
        <div className="flex flex-col sm:flex-row gap-4" data-id="element-2209">
          <div className="flex-1" data-id="element-2210">
            <Input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} leftIcon={<CalendarIcon className="w-4 h-4" data-id="element-2212" />} data-id="element-2211" />
          </div>
          <div className="flex-1" data-id="element-2213">
            <Select options={vendorOptions} value={vendorFilter} onChange={e => setVendorFilter(e.target.value)} data-id="element-2214" />
          </div>
          <button onClick={() => setShowCancelled(p => !p)} className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${showCancelled ? 'bg-rose-50 border-rose-300 text-rose-700' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`} data-id="element-2215">
            <span className={`w-2.5 h-2.5 rounded-full ${showCancelled ? 'bg-rose-500' : 'bg-gray-300'}`} data-id="element-2216" />
            Canceladas
          </button>
          <Button variant="secondary" onClick={() => {
          setDateFilter('');
          setVendorFilter('');
          setSelectedVisitId(null);
          setShowCancelled(false);
        }} data-id="element-2217">
            <FilterIcon className="w-4 h-4 mr-2" data-id="element-2218" /> Limpiar
          </Button>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-stagger-3" data-id="element-2219">
        {/* Map card */}
        <Card padding="none" className="lg:col-span-2 overflow-hidden" data-id="element-2220">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between" data-id="element-2221">
            <CardHeader title="Mapa de Visitas" subtitle={`${visitsWithGPS.length} ubicaciones registradas`} data-id="element-2222" />
            <div className="flex items-center gap-2" data-id="element-2223">
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1" data-id="element-2224">
                {([{
                key: 'streets',
                label: '🗺️ Mapa'
              }, {
                key: 'osm',
                label: '🗺️ OSM'
              }, {
                key: 'google',
                label: '🗺️ Google'
              }, {
                key: 'google-sat',
                label: '🛰️ Satélite'
              }, {
                key: 'dark',
                label: '🌙 Oscuro'
              }] as const).map(({
                key,
                label
              }) => <button key={key} onClick={() => setMapStyle(key as any)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${mapStyle === key ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-2225">
                    {label}
                  </button>)}
              </div>
              <Button variant="ghost" size="sm" onClick={resetView} data-id="element-2226">
                <MaximizeIcon className="w-4 h-4" data-id="element-2227" />
              </Button>
            </div>
          </div>

          {/* Map container — explicit height is critical */}
          <div style={{
          height: 500,
          position: 'relative',
          zIndex: 0
        }} data-id="element-2228">
            <LeafletMap onMapReady={handleMapReady} data-id="element-2229" />
          </div>

          {isDemo && <div className="px-4 py-2.5 bg-blue-50 border-t border-blue-100 flex items-center gap-2 text-xs text-blue-700" data-id="element-2230">
              <span className="inline-flex items-center justify-center w-4 h-4 bg-blue-200 rounded-full text-blue-800 font-bold text-[10px] flex-shrink-0" data-id="element-2231">
                i
              </span>
              <span data-id="element-2232">
                Mostrando <strong data-id="element-2233">datos demo 2026</strong> — Las ubicaciones
                reales aparecerán cuando los vendedores registren visitas con
                GPS.
              </span>
            </div>}

          <div className="p-3 border-t border-gray-100 flex items-center gap-6 text-xs text-gray-500" data-id="element-2234">
            <div className="flex items-center gap-1.5" data-id="element-2235">
              <div className="w-3 h-3 rounded-full bg-emerald-500" data-id="element-2236" />
              <span data-id="element-2237">Completada</span>
            </div>
            <div className="flex items-center gap-1.5" data-id="element-2238">
              <div className="w-3 h-3 rounded-full bg-amber-500" data-id="element-2239" />
              <span data-id="element-2240">En Progreso</span>
            </div>
            <div className="flex items-center gap-1.5" data-id="element-2241">
              <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-2242" />
              <span data-id="element-2243">Cancelada</span>
            </div>
          </div>
        </Card>

        {/* Sidebar list */}
        <Card padding="none" data-id="element-2244">
          <div className="p-4 border-b border-gray-100" data-id="element-2245">
            <CardHeader title="Ubicaciones Registradas" data-id="element-2246" />
          </div>
          <div className="divide-y divide-gray-100 max-h-[560px] overflow-y-auto" data-id="element-2247">
            {visitsWithGPS.length === 0 ? <div className="p-8 text-center text-gray-500" data-id="element-2248">
                <MapPinIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" data-id="element-2249" />
                <p data-id="element-2250">No hay ubicaciones registradas</p>
              </div> : visitsWithGPS.map(visit => <button key={visit.id} onClick={() => centerOnVisit(visit.id)} className={`w-full text-left p-3 transition-colors ${selectedVisitId === visit.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : 'hover:bg-gray-50 border-l-4 border-l-transparent'}`} data-id="element-2251">
                  <div className="flex items-start gap-3" data-id="element-2252">
                    <div className={`w-3 h-3 rounded-full mt-1.5 flex-shrink-0 ${statusBgClass(visit.estado)}`} data-id="element-2253" />
                    <div className="flex-1 min-w-0" data-id="element-2254">
                      <p className="font-medium text-gray-900 truncate" data-id="element-2255">
                        {visit.clienteNombre}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-1" data-id="element-2256">
                        <UserIcon className="w-3 h-3 flex-shrink-0" data-id="element-2257" />
                        <span className="truncate" data-id="element-2258">{visit.vendedorNombre}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5" data-id="element-2259">
                        <ClockIcon className="w-3 h-3 flex-shrink-0" data-id="element-2260" />
                        <span data-id="element-2261">
                          {fmtDate(visit.fecha)} · {fmtTime(visit.horaLlegada)}
                        </span>
                      </div>
                      {visit.ubicacionLlegada && <div className="flex items-center gap-2 mt-1" data-id="element-2262">
                          <p className="text-xs text-gray-400 font-mono" data-id="element-2263">
                            {visit.ubicacionLlegada.lat.toFixed(4)},{' '}
                            {visit.ubicacionLlegada.lng.toFixed(4)}
                          </p>
                          <a href={`https://www.google.com/maps/dir/?api=1&destination=${visit.ubicacionLlegada.lat},${visit.ubicacionLlegada.lng}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-[10px] font-semibold text-blue-500 hover:text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded" data-id="element-2264">
                            🧭 Ir
                          </a>
                        </div>}
                      <div className="flex items-center gap-3 mt-1" data-id="element-2265">
                        {visit.cobro.huboCobo && <span className="text-xs font-medium text-emerald-600" data-id="element-2266">
                            {fmtCurrency(visit.cobro.montoCobrado)}
                          </span>}
                        {visit.venta.huboVenta && <span className="text-xs font-medium text-blue-600" data-id="element-2267">
                            {fmtCurrency(visit.venta.montoVenta)}
                          </span>}
                      </div>
                    </div>
                    <Badge variant={visit.estado === 'completada' ? 'success' : visit.estado === 'en_progreso' ? 'warning' : 'danger'} size="sm" data-id="element-2268">
                      {visit.estado === 'completada' && visit.tiempoTotal ? `${visit.tiempoTotal}m` : visit.estado === 'en_progreso' ? 'En curso' : 'Cancelada'}
                    </Badge>
                  </div>
                </button>)}
          </div>
        </Card>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-stagger-4" data-id="element-2269">
        <Card padding="sm" className="text-center" data-id="element-2270">
          <p className="text-2xl font-bold text-gray-900 font-heading" data-id="element-2271">
            {visitsWithGPS.length}
          </p>
          <p className="text-sm text-gray-500" data-id="element-2272">Visitas con GPS</p>
        </Card>
        <Card padding="sm" className="text-center" data-id="element-2273">
          <p className="text-2xl font-bold text-emerald-600 font-heading" data-id="element-2274">
            {visitsWithGPS.filter(v => v.estado === 'completada').length}
          </p>
          <p className="text-sm text-gray-500" data-id="element-2275">Completadas</p>
        </Card>
        <Card padding="sm" className="text-center" data-id="element-2276">
          <p className="text-2xl font-bold text-amber-600 font-heading" data-id="element-2277">
            {visitsWithGPS.filter(v => v.estado === 'en_progreso').length}
          </p>
          <p className="text-sm text-gray-500" data-id="element-2278">En Progreso</p>
        </Card>
        <Card padding="sm" className="text-center" data-id="element-2279">
          <p className="text-2xl font-bold text-gray-900 font-heading" data-id="element-2280">
            {new Set(visitsWithGPS.map(v => v.vendedorId)).size}
          </p>
          <p className="text-sm text-gray-500" data-id="element-2281">Vendedores Activos</p>
        </Card>
      </div>
    </div>;
}